"use client";

import {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useAuth } from "@clerk/nextjs";
import { type PricelistForMemberships } from "@/utils/getPricelistForUserMemberships";
import { Logger } from "@cappelendamm/logger/dist/logger";

type PricelistContextState = {
  applicablePricelists: PricelistForMemberships[];
};

export const PricelistContext = createContext<PricelistContextState>({
  applicablePricelists: [],
});

export function PricelistContextProvider({
  children,
  initialPricelists,
}: {
  children: ReactNode;
  initialPricelists: PricelistForMemberships[];
}) {
  const { userId, getToken } = useAuth();
  const [applicablePricelists, setApplicablePricelists] =
    useState<PricelistForMemberships[]>(initialPricelists);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>();

  const loadPricelists = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await fetch("/api/user-info", {
        method: "GET",
        cache: "no-store",
        credentials: "include",
        signal,
      });

      if (!response.ok) {
        setLoadedForUserId(null);
        return;
      }

      const data = (await response.json()) as {
        applicablePricelists?: PricelistForMemberships[];
        userId?: string | null;
      };

      setApplicablePricelists(
        Array.isArray(data.applicablePricelists)
          ? data.applicablePricelists
          : [],
      );
      setLoadedForUserId(data.userId ?? null);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      Logger.error("Failed to fetch applicable pricelists", error);
      setLoadedForUserId(null);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadPricelists(controller.signal);
    return () => controller.abort();
  }, [loadPricelists]);

  useEffect(() => {
    if (
      loadedForUserId === undefined ||
      userId === undefined ||
      userId === loadedForUserId
    ) {
      return;
    }

    const controller = new AbortController();
    void getToken()
      .catch(() => null)
      .then(() => loadPricelists(controller.signal));
    return () => controller.abort();
  }, [getToken, loadPricelists, loadedForUserId, userId]);

  return (
    <PricelistContext value={{ applicablePricelists }}>
      {children}
    </PricelistContext>
  );
}

export function usePricelist() {
  return use(PricelistContext);
}
